<template>
    <div>
      <!-- Rank Summary Cards -->
      <v-row class="mt-2 mb-3">
        <v-col cols="12" sm="6" :md="Math.min(2, Math.floor(12 / Math.max(rankCards.length, 1)))" class="pa-1" v-for="rank in rankCards" :key="rank.rank">
          <v-card class="rank-card" outlined>
            <v-card-text class="text-center pa-3">
              <div class="rank-percentage">{{ rank.percentage }}</div>
              <div class="d-flex justify-center align-center mt-1">
                <div class="rank-count mr-1">{{ rank.count }}</div>
                <div class="rank-emp-label">{{ rank.count === 1 ? "employee" : "employees" }}</div>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="rank-name">RANK {{ rank.rank }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Search + Export -->
      <v-row class="mb-3 align-center">
        <v-col cols="12" md="4" class="py-0">
          <v-text-field
            v-model="localSearchQuery"
            dense
            hide-details
            outlined
            append-icon="mdi-magnify"
            clearable
            placeholder="Search..."
            @input="updateSearchQuery"
          ></v-text-field>
        </v-col>
        <v-col cols="auto" class="py-0">
          <button type="button" class="export-icon-btn export-icon-pdf" @click="exportPDF" title="Export PDF">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/><polyline points="9 9 10 9"/></svg>
            PDF
          </button>
          <button type="button" class="export-icon-btn export-icon-csv" @click="exportCSV" title="Export CSV">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
            CSV
          </button>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-card outlined>
        <v-data-table
          :headers="tableHeaders"
          :items="filteredTableData"
          class="centered-table"
          dense
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.staffcode }}</td>
              <td>{{ item.staffname }}</td>
              <td>{{ item.reviewfromdate }}</td>
              <td>{{ item.reviewuntildate }}</td>
              <td>{{ item.score != null ? parseFloat(item.score).toFixed(2) : '' }}</td>
              <td>{{ item.rank }}</td>
              <td>{{ item.finalrank }}</td>
              <td>{{ item.finalincreamt != null ? parseFloat(item.finalincreamt).toFixed(2) : '' }}</td>
              <td>{{ item.finalbonusamt != null ? parseFloat(item.finalbonusamt).toFixed(2) : '' }}</td>
              <td>{{ item.companycode }}</td>
              <td>{{ item.branchcode }}</td>
              <td>{{ item.departmentcode }}</td>
              <td>{{ item.divisioncode }}</td>
              <td>{{ item.sectioncode }}</td>
              <td>{{ item.unitcode }}</td>
              <td>{{ item.gradecode }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </template>
  
  <script>
//   import moment from 'moment';
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';
  
  export default {
    name: 'MyStaffTable',
    props: {
      tableData: {
        type: Array,
        required: true,
        default: () => []
      },
      searchQuery: {
        type: String,
        default: ''
      },
      parentObject: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        publicPath: process.env.BASE_URL,
        localSearchQuery: this.searchQuery,
        tableHeaders: [
          { text: 'Staff ID', value: 'staffcode', sortable: true },
          { text: 'Name', value: 'staffname', sortable: true, width: '170px' },
          { text: 'Review From Date', value: 'reviewfromdate', sortable: true, width: '140px' },
          { text: 'Review Until Date', value: 'reviewuntildate', sortable: true, width: '140px' },
          { text: 'Score', value: 'score', sortable: true },
          { text: 'Rank', value: 'rank', sortable: true },
          { text: 'Final Rank', value: 'finalrank', sortable: true },
          { text: 'Final Increment', value: 'finalincreamt', sortable: true },
          { text: 'Final Bonus', value: 'finalbonusamt', sortable: true },
          { text: 'Company', value: 'companycode', sortable: true },
          { text: 'Branch', value: 'branchcode', sortable: true },
          { text: 'Department', value: 'departmentcode', sortable: true },
          { text: 'Division', value: 'divisioncode', sortable: true },
          { text: 'Section', value: 'sectioncode', sortable: true },
          { text: 'Unit', value: 'unitcode', sortable: true },
          { text: 'Grade', value: 'gradecode', sortable: true },
        ],
      };
    },
    computed: {
      rankCards() {
        return this.calculateRankCards(this.tableData);
      },
      filteredTableData() {
        if (!this.localSearchQuery) return this.tableData;
        
        const query = this.localSearchQuery.toLowerCase();
        return this.tableData.filter(item => {
          return Object.values(item).some(val => 
            String(val).toLowerCase().includes(query)
          );
        });
      }
    },
    watch: {
      searchQuery(newVal) {
        this.localSearchQuery = newVal;
      }
    },
    methods: {
      updateSearchQuery(value) {
        this.$emit('update:searchQuery', value);
      },
      calculateRankCards(staffList) {
        const total = staffList.length;
        const rankCounts = {};

        staffList.forEach((staff) => {
          const r = staff.rank;
          if (r !== null && r !== undefined && r !== '') {
            rankCounts[r] = (rankCounts[r] || 0) + 1;
          }
        });

        return Object.keys(rankCounts).sort().map(rank => {
          const count = rankCounts[rank];
          const percentage = total === 0 ? '0%' : ((count / total) * 100).toFixed(1) + '%';
          return { rank, percentage, count };
        });
      },
      exportPDF() {
        console.log("Exporting to PDF...");
  
        const doc = new jsPDF({
          orientation: 'landscape',
        });
  
        const head = [
          { title: 'Staff ID', dataKey: 'staffcode' },
          { title: 'Name', dataKey: 'staffname' },
          { title: 'Review From Date', dataKey: 'reviewfromdate' },
          { title: 'Review Until Date', dataKey: 'reviewuntildate' },
          { title: 'Score', dataKey: 'score' },
          { title: 'Rank', dataKey: 'rank' },
          { title: 'Final Rank', dataKey: 'finalrank' },
          { title: 'Final Increment', dataKey: 'finalincreamt' },
          { title: 'Final Bonus', dataKey: 'finalbonusamt' },
          { title: 'Company', dataKey: 'companycode' },
          { title: 'Branch', dataKey: 'branchcode' },
          { title: 'Department', dataKey: 'departmentcode' },
          { title: 'Division', dataKey: 'divisioncode' },
          { title: 'Section', dataKey: 'sectioncode' },
          { title: 'Unit', dataKey: 'unitcode' },
          { title: 'Grade', dataKey: 'gradecode' },
        ];
  
        const body = this.filteredTableData.map(item => ({
          staffcode: item.staffcode,
          staffname: item.staffname,
          reviewfromdate: item.reviewfromdate,
          reviewuntildate: item.reviewuntildate,
          score: item.score != null ? parseFloat(item.score).toFixed(2) : '',
          rank: item.rank,
          finalrank: item.finalrank,
          finalincreamt: item.finalincreamt != null ? parseFloat(item.finalincreamt).toFixed(2) : '',
          finalbonusamt: item.finalbonusamt != null ? parseFloat(item.finalbonusamt).toFixed(2) : '',
          companycode: item.companycode,
          branchcode: item.branchcode,
          departmentcode: item.departmentcode,
          divisioncode: item.divisioncode,
          sectioncode: item.sectioncode,
          unitcode: item.unitcode,
          gradecode: item.gradecode,
        }));

        doc.text('Reward Report', 10, 10);
        doc.text(`Period: ${this.parentObject.list.startDate} to ${this.parentObject.list.endDate}`, 10, 20);
  
        doc.autoTable(head, body, {
          startY: 30,
          margin: { top: 10 },
          theme: 'striped',
          headStyles: {
            fontSize: 8,
            halign: 'center'
          },
          bodyStyles: {
            fontSize: 8,
            halign: 'center'
          },
        });
  
        doc.save(`staff-list-reward-${new Date().toLocaleDateString()}.pdf`);
      },
      objectToCSV(data) {
        const csvRows = [];
        const headers = this.tableHeaders.map(header => header.text);
        csvRows.push(headers.join(","));
  
        for (const row of data) {
          const values = this.tableHeaders.map((header) => {
            const escaped = ("" + row[header.value]).replace(/"/g, '\\"');
            return `"${escaped}"`;
          });
          csvRows.push(values.join(","));
        }
        return csvRows.join("\n");
      },
      download(data) {
        const blob = new Blob([decodeURIComponent('%ef%bb%bf'), data], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
  
        const a = document.createElement("a");
        a.setAttribute("hidden", "");
        a.setAttribute("href", url);
        a.setAttribute("download", "reward-table.csv");
  
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      },
      exportCSV() {
        console.log("Exporting to CSV...");
        
        const data = this.filteredTableData.map(item => ({
          staffcode: item.staffcode,
          staffname: item.staffname,
          reviewfromdate: item.reviewfromdate,
          reviewuntildate: item.reviewuntildate,
          score: item.score != null ? parseFloat(item.score).toFixed(2) : '',
          rank: item.rank,
          finalrank: item.finalrank,
          finalincreamt: item.finalincreamt != null ? parseFloat(item.finalincreamt).toFixed(2) : '',
          finalbonusamt: item.finalbonusamt != null ? parseFloat(item.finalbonusamt).toFixed(2) : '',
          companycode: item.companycode,
          branchcode: item.branchcode,
          departmentcode: item.departmentcode,
          divisioncode: item.divisioncode,
          sectioncode: item.sectioncode,
          unitcode: item.unitcode,
          gradecode: item.gradecode,
        }));

        const csvData = this.objectToCSV(data);
        this.download(csvData);
      },
    }
  };
  </script>
  
  <style scoped>
  .rank-card {
    border-radius: 10px !important;
    border-color: #C7D7FF !important;
    background: #EEF2FF !important;
    transition: transform 0.2s;
    min-height: 110px;
  }

  .rank-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(18,97,160,0.12) !important;
  }

  .rank-percentage {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1261a0;
    letter-spacing: normal;
  }

  .rank-count {
    font-size: 1rem;
    font-weight: 700;
    color: #1a3a5c;
    letter-spacing: normal;
  }

  .rank-emp-label {
    font-size: 0.72rem;
    color: #546e7a;
    letter-spacing: normal;
  }

  .rank-name {
    font-size: 0.78rem;
    font-weight: 700;
    color: #1261a0;
    letter-spacing: normal;
  }

  .fbtn {
    min-width: 80px !important;
    height: 36px !important;
    border-radius: 8px !important;
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.02em !important;
    box-shadow: none !important;
  }

  .fbtn-secondary {
    background: #EEF2FF !important;
    color: #4F6BFF !important;
  }

  .fbtn-secondary:hover {
    background: #4F6BFF !important;
    color: #fff !important;
  }
  </style>

  <style>
  .centered-table th {
    text-align: left !important;
  }

  .centered-table td {
    text-align: left;
  }

  .export-icon-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 8px; border: none;
    font-size: 0.8rem; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: all 0.15s; margin-right: 4px;
  }
  .export-icon-pdf { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }
  .export-icon-pdf:hover { background: #DC2626; color: #fff; border-color: #DC2626; }
  .export-icon-csv { background: #ECFDF5; color: #059669; border: 1px solid #A7F3D0; }
  .export-icon-csv:hover { background: #059669; color: #fff; border-color: #059669; }
  </style>